import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";
import { type FC, useState } from "react";
import { GoalPortfolioValueDrawer } from "web_myplann/Goals/Components/GoalPortfolioValueDrawer";
import { SolutionItemActionDrawer } from "web_myplann/Goals/Components/SolutionItemActionDrawer";
import { SolutionRow } from "web_myplann/Goals/Components/SolutionRow";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { withLockGuard } from "web_myplann/Goals/Helpers/withLockGuard";
import { useGoalDetailSolutionItem } from "web_myplann/Goals/Hooks/useGoalDetailSolutionItem";
import type {
	CurrencyEnum,
	GoalPortfolioResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ConfirmDrawer } from "web_myplann/Shared/Components/ConfirmDrawer";

type Props = {
	portfolio: GoalPortfolioResponseDTO;
	hasDivider: boolean;
	dueYear?: number;
	readOnly?: boolean;
	currencyCode?: CurrencyEnum;
	onUnpin: (portfolioId: number) => void;
	onEditAllocation?: (portfolio: GoalPortfolioResponseDTO) => void;
	onLockedAction?: (() => void) | null;
};

export const GoalDetailSolutionItem: FC<Props> = ({
	portfolio,
	hasDivider,
	dueYear,
	readOnly,
	currencyCode,
	onUnpin,
	onEditAllocation,
	onLockedAction,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const {
		title,
		subtitle,
		allocationPercent,
		showAllocation,
		displayValue,
		expectedCoverage,
		showProjection,
	} = useGoalDetailSolutionItem(portfolio, dueYear);

	const [actionOpen, setActionOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [valueOpen, setValueOpen] = useState(false);
	const closeAction = () => setActionOpen(false);
	const isActionable =
		!readOnly && !portfolio.archived && portfolio.portfolioId != null;

	const handleUpdateValue = withLockGuard(onLockedAction ?? null, () =>
		setValueOpen(true),
	);

	return (
		<>
			<SolutionRow
				hasDivider={hasDivider}
				onClick={isActionable ? () => setActionOpen(true) : undefined}
			>
				<Stack flex={1} minWidth={0} gap={0.2}>
					<Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
						<Typography
							variant="bodyMedium"
							color={portfolio.archived ? "text.secondary" : undefined}
							noWrap
						>
							{title}
						</Typography>
						{portfolio.archived && (
							<Chip
								variant="tag"
								label={t(Resources.Goals.Detail.Solution.archivedBadge)}
							/>
						)}
						{showAllocation && (
							<Chip
								variant="tag"
								label={t(Resources.Goals.Detail.Solution.allocationLabel, {
									percent: allocationPercent,
								})}
								sx={{ fontVariantNumeric: "tabular-nums" }}
							/>
						)}
					</Stack>
					{subtitle && (
						<Typography variant="caption" color="text.secondary" noWrap>
							{subtitle}
						</Typography>
					)}
				</Stack>
				<Stack alignItems="flex-end" flexShrink={0} gap={0.4}>
					<Typography variant="bodyStrong" tabular>
						{t(Resources.Goals.Detail.Solution.valueLabel, {
							amount: formatAmount(displayValue),
						})}
					</Typography>
					{showProjection && (
						<Typography
							variant="caption"
							color={theme.palette.primary.main}
							tabular
						>
							{t(Resources.Goals.Detail.Solution.expectedInYear, {
								amount: formatAmount(expectedCoverage),
								year: dueYear,
							})}
						</Typography>
					)}
				</Stack>
				{!readOnly && !isActionable && (
					<Box width={theme.iconSize.sm} flexShrink={0} />
				)}
			</SolutionRow>
			<SolutionItemActionDrawer
				open={actionOpen}
				portfolio={portfolio}
				onClose={closeAction}
				onRequestUnpin={() => setConfirmOpen(true)}
				onUpdateValue={handleUpdateValue}
				onEditAllocation={onEditAllocation}
			/>
			<GoalPortfolioValueDrawer
				open={valueOpen}
				portfolio={portfolio}
				currencyCode={currencyCode}
				onClose={() => setValueOpen(false)}
			/>
			<ConfirmDrawer
				open={confirmOpen}
				title={t(Resources.Goals.Detail.Solution.Unpin.title)}
				description={t(Resources.Goals.Detail.Solution.Unpin.description, {
					name: portfolio.name || title,
				})}
				confirmLabel={t(Resources.Goals.Detail.Solution.Unpin.confirm)}
				cancelLabel={t(Resources.Goals.Detail.Solution.Unpin.cancel)}
				destructive
				onClose={() => setConfirmOpen(false)}
				onConfirm={() => {
					setConfirmOpen(false);
					if (portfolio.portfolioId == null) return;
					onUnpin(portfolio.portfolioId);
				}}
			/>
		</>
	);
};
